// hello
export function deduplicateArray<T>(array: T[] | undefined) {
  if (array === undefined) return undefined;
  return Array.from(new Set<T>(array));
}

/**
 * Chunks an array into pieces of size X.
 * @param {T[]} arr
 * @param {number} size
 * @returns {T[][]}
 */
export function chunk<T>(arr: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(arr.length / size) }, (v, i) => arr.slice(i * size, i * size + size));
}

/**
 * Takes a predicate and a list of values and returns a a tuple (2-item array),
 *  with each item containing the subset of the list that matches the predicate
 *  and the complement of the predicate respectively
 *
 * @sig (T -> Boolean, T[]) -> [T[], T[]]
 *
 * @param {Array} arr The list to partition
 * @param {Function} predicate A predicate to determine which side the element belongs to.
 *
 * Inspired by the Ramda function of the same name
 * @see https://ramdajs.com/docs/#partition
 *
 * @example
 *
 *     const isNegative: (n: number) => boolean = n => n < 0
 *     const numbers = [1, 2, -4, -7, 4, 22]
 *     partition(numbers, isNegative)
 *     // => [ [-4, -7], [1, 2, 4, 22] ]
 */
export function partition<T>(arr: Array<T>, predicate: (val: T) => boolean): [Array<T>, Array<T>] {
  const partitioned: [Array<T>, Array<T>] = [[], []];
  arr.forEach((val: T) => {
    const partitionIndex: 0 | 1 = predicate(val) ? 0 : 1;
    partitioned[partitionIndex].push(val);
  });
  return partitioned;
}

/**
 * Groups an array using a getGroupKey function, returning an object.
 * @param {T[]} list
 * @param {(item: T) => K} getGroupKey
 * @returns {T}
 */
export function groupBy<T, K extends keyof any>(list: T[], getGroupKey: (item: T) => K) {
  return list.reduce(
    (previous, currentItem) => {
      const group = getGroupKey(currentItem);
      if (!previous[group]) previous[group] = [];
      previous[group].push(currentItem);
      return previous;
    },
    {} as Record<K, T[]>
  );
}

export function arrayify<T>(something: T | T[]): T[] {
  if (!Array.isArray(something)) {
    return [something];
  }
  return something;
}

export function arrayToMap<T extends Record<string, any>>(ent: T[], fieldToUseAsKey: keyof T): Map<string, T> {
  return ent.reduce((map, item) => {
    map.set(item[fieldToUseAsKey] as string, item);
    return map;
  }, new Map<string, T>());
}

export function arrayToMapViaMapper<T extends Record<string, any>>(
  ent: T[],
  mapperFunction: (entity: T) => string
): Map<string, T> {
  return ent.reduce((map, item) => {
    map.set(mapperFunction(item), item);
    return map;
  }, new Map<string, T>());
}
